import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useChatStore } from '../stores/chatStore';
import MessageList from '../components/MessageList';
import ThinkingPanel from '../components/ThinkingPanel';

const ChatPage: React.FC = () => {
  const {
    currentMessages,
    progressEvents,
    isSending,
    isStreaming,
    error,
    sendMessage,
    clearError,
  } = useChatStore();

  const [inputMessage, setInputMessage] = useState('');
  const [reportLength, setReportLength] = useState<'brief' | 'standard' | 'comprehensive' | 'detailed'>('standard');
  const [detailLevel, setDetailLevel] = useState<'summary' | 'moderate' | 'extensive'>('moderate');
  const [citationStyle, setCitationStyle] = useState<'APA' | 'MLA' | 'Chicago' | 'Nature'>('APA');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentMessages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const message = inputMessage.trim();

    // Add options to message if it looks like a report request
    const isReportRequest = message.toLowerCase().includes('report') ||
                           message.toLowerCase().includes('summary') ||
                           message.toLowerCase().includes('review');

    const messageWithOptions = isReportRequest
      ? `${message}\n\n[Report Options: Length=${reportLength}, Detail=${detailLevel}, Citations=${citationStyle}]`
      : message;

    setInputMessage('');

    try {
      await sendMessage(null, messageWithOptions, 'New Chat');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Container maxWidth="lg" sx={{ minHeight: '100%', py: 2 }}>
      <Box sx={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">Literature Research Chat</Typography>
        </Paper>

        {/* Report Options Panel */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Report Options
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Report Length</InputLabel>
              <Select
                value={reportLength}
                label="Report Length"
                onChange={(e) => setReportLength(e.target.value as any)}
              >
                <MenuItem value="brief">Brief (1-2 pages)</MenuItem>
                <MenuItem value="standard">Standard (3-5 pages)</MenuItem>
                <MenuItem value="comprehensive">Comprehensive (6-10 pages)</MenuItem>
                <MenuItem value="detailed">Detailed (10+ pages)</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Detail Level</InputLabel>
              <Select
                value={detailLevel}
                label="Detail Level"
                onChange={(e) => setDetailLevel(e.target.value as any)}
              >
                <MenuItem value="summary">Summary Only</MenuItem>
                <MenuItem value="moderate">Moderate Detail</MenuItem>
                <MenuItem value="extensive">Extensive Detail</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Citation Style</InputLabel>
              <Select
                value={citationStyle}
                label="Citation Style"
                onChange={(e) => setCitationStyle(e.target.value as any)}
              >
                <MenuItem value="APA">APA 7th</MenuItem>
                <MenuItem value="MLA">MLA 9th</MenuItem>
                <MenuItem value="Chicago">Chicago 17th</MenuItem>
                <MenuItem value="Nature">Nature</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Current selection: {reportLength} report with {detailLevel} detail in {citationStyle} format
            </Typography>
          </Box>
        </Paper>

        {/* Thinking Panel */}
        {(progressEvents.length > 0 || isStreaming) && (
          <ThinkingPanel events={progressEvents} isActive={isStreaming} />
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        {/* Messages */}
        <Paper
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 400,
          }}
          className="chat-messages"
        >
          {currentMessages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Welcome to Literature Research
              </Typography>
              <Typography variant="body2">
                Ask me to search and analyze scientific literature on any topic.
              </Typography>
              <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {[
                  'Recent advances in CRISPR gene editing',
                  'Literature on mRNA vaccine technology',
                  'CAR-T cell therapy in oncology',
                  'AlphaFold protein structure prediction',
                ].map((example) => (
                  <Typography
                    key={example}
                    variant="caption"
                    sx={{
                      px: 2,
                      py: 1,
                      bgcolor: 'action.hover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                    onClick={() => setInputMessage(example)}
                  >
                    {example}
                  </Typography>
                ))}
              </Box>
            </Box>
          ) : (
            <>
              <MessageList
                messages={currentMessages}
                sessionTitle="Literature Research Chat"
                sessionId="current"
                reportOptions={{
                  length: reportLength,
                  detail: detailLevel,
                  citation: citationStyle,
                }}
              />
              <div ref={messagesEndRef} />
            </>
          )}
        </Paper>

        {/* Input */}
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask about any research topic..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending}
              sx={{
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isSending}
              sx={{ alignSelf: 'flex-end', mb: 0.5 }}
            >
              {isSending ? (
                <CircularProgress size={24} />
              ) : (
                <SendIcon />
              )}
            </IconButton>
          </Box>
          {isStreaming && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Researching and analyzing...
            </Typography>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ChatPage;
